import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Examen } from '../../../types/examenes';

interface ExamenesPopularesProps {
  examenes: Examen[];
  facturas: any[]; // Usamos any para evitar problemas de tipos
}

// Colores para cada barra
const COLORES = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const ExamenesPopulares: React.FC<ExamenesPopularesProps> = ({ examenes, facturas }) => {
  const datos = React.useMemo(() => {
    if (!facturas || facturas.length === 0) return [];

    const frecuencia: Record<string, number> = {};

    facturas.forEach(factura => {
      if (!factura.examenes) return;
      
      factura.examenes.forEach((item: any) => {
        let nombreExamen = '';
        
        // Caso 1: examen es un objeto con nombre directamente
        if (typeof item.examen === 'object' && item.examen !== null) {
          nombreExamen = item.examen.nombre || item.examen.codigo || item.examen._id || '';
        } 
        // Caso 2: es string (ID), buscar en la lista
        else if (typeof item.examen === 'string') {
          const encontrado = examenes.find(e => e._id === item.examen || e.id === item.examen);
          nombreExamen = encontrado?.nombre || encontrado?.codigo || item.examen;
        }
        
        if (nombreExamen && item.cantidad) {
          if (!frecuencia[nombreExamen]) {
            frecuencia[nombreExamen] = 0;
          }
          frecuencia[nombreExamen] += item.cantidad;
        }
      });
    });

    return Object.entries(frecuencia)
      .map(([nombre, cantidad]) => ({ nombre, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 5);
  }, [examenes, facturas]);

  // Tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
          <p className="font-medium">{data.nombre}</p>
          <p className="text-purple-400">{data.cantidad} solicitudes</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-[300px]">
      {datos.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-400">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-sm font-medium text-gray-500">Sin datos</p>
            <p className="text-xs text-gray-400 mt-1">Crea facturas para ver estadísticas</p>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={datos} layout="vertical" margin={{ left: 10 }}>
            <XAxis 
              type="number" 
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              dataKey="nombre"
              type="category"
              width={140}
              tick={{ fontSize: 11, fill: '#374151' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }} />
            <Bar dataKey="cantidad" radius={[0, 6, 6, 0]} animationDuration={800}>
              {datos.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORES[index % COLORES.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ExamenesPopulares;